import React, { useEffect, useCallback } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeft, ChevronRight } from 'react-feather';

import './EmblaSlider.css';

// Make sure to add EmblaSlider.css for styling
interface EmblaSliderProps {
  slides: React.ReactNode[];
  slidesToShow?: number;
  className?: string;
  navClassName?: string;
  onSlideChange?: (index: number) => void;
}

const EmblaSlider: React.FC<EmblaSliderProps> = ({
  slides,
  slidesToShow = 4,
  className = '',
  navClassName = '',
  onSlideChange,
}) => {
  // useEmblaCarousel returns [viewportRef, emblaApi]
  const [viewportRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: 'start',
    slidesToScroll: slidesToShow,
    containScroll: 'trimSnaps',
  });

  // Track which group of slides is visible
  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);

  // Jump to initial (last group) on mount
  useEffect(() => {
    if (emblaApi && slides.length > slidesToShow) {
      const lastGroupIndex = Math.floor((slides.length - 1) / slidesToShow);
      emblaApi.scrollTo(lastGroupIndex * slidesToShow);
    }
  }, [emblaApi, slides.length, slidesToShow]);

  // Update arrow state
  const updateButtons = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
    const firstVisibleSlideIndex = emblaApi.selectedScrollSnap();
    onSlideChange?.(firstVisibleSlideIndex);
  }, [emblaApi, onSlideChange]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on('select', updateButtons);
    updateButtons();
    return () => {
      emblaApi.off('select', updateButtons);
    };
  }, [emblaApi, updateButtons]);

  // Handlers
  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  return (
    <div className={`embla ${className}`}>
      <div className="embla__viewport" ref={viewportRef}>
        <div className="embla__container">
          {slides.map((slide, idx) => (
            <div className="embla__slide" style={{ flex: `0 0 ${100 / slidesToShow}%` }} key={idx}>
              {slide}
            </div>
          ))}
        </div>
      </div>
      <button
        className={`embla__arrow embla__arrow--prev btn ${navClassName}`}
        onClick={scrollPrev}
        disabled={!canScrollPrev}
        style={{ visibility: canScrollPrev ? 'visible' : 'hidden' }}
        aria-label="Previous"
        aria-hidden={!canScrollPrev}
      >
        <ChevronLeft size={24} />
      </button>
      <button
        className={`embla__arrow embla__arrow--next btn ${navClassName}`}
        onClick={scrollNext}
        disabled={!canScrollNext}
        style={{ visibility: canScrollNext ? 'visible' : 'hidden' }}
        aria-label="Next"
        aria-hidden={!canScrollNext}
      >
        <ChevronRight size={24} />
      </button>
    </div>
  );
};

export default EmblaSlider;
